import React, { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
} from "recharts";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import CustomChartTooltip from "./CustomChartTooltip/CustomChartTooltip";
import {
  CHART_COLOR_PALETTE,
  CHART_TICK_COLOR,
  CHART_AXIS_STROKE,
  CHART_TOOLTIP_CURSOR,
} from "../charts/ChartPalette";

export const MULTI_METRIC_COLORS = CHART_COLOR_PALETTE;

type MetricConfig = {
  key: string;
  label: string;
};

type MultiMetricBarDatum = {
  name: string;
  [key: string]: string | number | null | undefined;
};

type MultiMetricBarChartProps = {
  chartData: MultiMetricBarDatum[];
  metrics: MetricConfig[];
  formatTooltip?: (_value: number, _key: string) => string;
  tickFormatter?: (_value: number) => string;
};

type MultiLineXAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
};

const ITEMS_PER_PAGE = 10;

const MultiLineXAxisTick: React.FC<MultiLineXAxisTickProps> = ({ x = 0, y = 0, payload }) => {
  const text = String(payload?.value ?? "");
  const maxLineLength = 12;

  const wrapText = (str: string, maxLen: number) => {
    const words = str.split(" ");
    const lines: string[] = [];
    let currentLine = "";

    words.forEach(word => {
      if ((currentLine + " " + word).trim().length <= maxLen) {
        currentLine += ` ${word}`;
      } else {
        lines.push(currentLine.trim());
        currentLine = word;
      }
    });

    if (currentLine.length > 0) {
      lines.push(currentLine.trim());
    }

    return lines;
  };

  const lines = wrapText(text, maxLineLength);
  const labelYOffset = 8;

  return (
    <g transform={`translate(${x},${y + labelYOffset})`}>
      <text textAnchor="middle" fill={CHART_TICK_COLOR} fontSize={9}>
        {lines?.map((line, index) => (
          <tspan key={index} x={0} dy={index === 0 ? 0 : 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const MultiMetricBarChart: React.FC<MultiMetricBarChartProps> = ({
  chartData,
  metrics,
  formatTooltip,
  tickFormatter,
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);

  const paginatedData = useMemo(() => {
    if (!Array.isArray(chartData)) {
      return [];
    }
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return chartData.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [chartData, currentPage]);

  const totalPages = Math.ceil((chartData?.length ?? 0) / ITEMS_PER_PAGE);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="chart-section-overview">
      {chartData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={paginatedData}
              margin={{ top: 10, right: 10, bottom: 15, left: 10 }}
              barCategoryGap="10%"
              barGap={2}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke={CHART_AXIS_STROKE}
              />

              <XAxis
                dataKey="name"
                padding={{ left: 10, right: 10 }}
                interval={0}
                height={45}
                tick={<MultiLineXAxisTick />}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: CHART_TICK_COLOR, fontSize: 12 }}
                tickFormatter={tickFormatter}
              />

              <Tooltip
                cursor={{ fill: CHART_TOOLTIP_CURSOR }}
                content={<CustomChartTooltip formatTooltip={formatTooltip} />}
              />

              {metrics?.map((metric, index) => (
                <Bar
                  key={metric.key}
                  dataKey={metric.key}
                  name={metric.label}
                  radius={[4, 4, 0, 0]}
                  barSize={44}
                >
                  {paginatedData?.map((_, i) => (
                    <Cell
                      key={`cell-${metric.key}-${i}`}
                      fill={CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length]}
                    />
                  ))}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>

          {/* Pagination */}
          {chartData?.length > ITEMS_PER_PAGE && (
            <div className="chart-pagination-wrapper">
              <div className="campaign-dashboard__pagination-controls">
                <button
                  type="button"
                  className="campaign-dashboard__pagination-btn"
                  aria-label={t("pagination.previousPage")}
                  disabled={currentPage <= 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  <ChevronLeft size={16} aria-hidden />
                </button>
                <button
                  type="button"
                  className="campaign-dashboard__pagination-btn"
                  aria-label={t("pagination.nextPage")}
                  disabled={currentPage >= totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  <ChevronRight size={16} aria-hidden />
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="no-data-container">
          <p>No performance data available</p>
        </div>
      )}
    </div>
  );
};

export default MultiMetricBarChart;